import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useUserGroupsInfiniteQuery } from "@/api/queries/admin/UserGroupQueries";

type UserGroupSelectProps = {
    value?: string[];
    onChange?: (value: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
};

export default function UserGroupSelect({
    value = [],
    onChange,
    placeholder = "Select user groups...",
    disabled = false,
}: UserGroupSelectProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const queryResult = useUserGroupsInfiniteQuery({
        limit: 10,
        filter: search ? { search } : undefined,
    });

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
        isLoading,
    } = queryResult;

    const userGroups = data?.pages.flatMap((page) => page.data) ?? [];
    const selectedGroups = userGroups.filter((g) => g.id && value.includes(g.id));

    const { ref: lastGroupRef, inView } = useInView({ threshold: 0.3 });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: refetch when search changes
    useEffect(() => {
        refetch();
    }, [search, refetch]);

    const handleSelect = (groupId: string) => {
        const newValue = value.includes(groupId)
            ? value.filter((id) => id !== groupId)
            : [...value, groupId];
        onChange?.(newValue);
    };

    const handleRemove = (groupId: string) => {
        onChange?.(value.filter((id) => id !== groupId));
    };

    return (
        <div className="space-y-2">
            <Popover onOpenChange={setOpen} open={open}>
                <PopoverTrigger asChild>
                    <Button
                        className="w-full justify-between"
                        role="combobox"
                        variant="outline"
                        disabled={disabled}
                    >
                        {value.length > 0
                            ? `${value.length} group${value.length > 1 ? "s" : ""} selected`
                            : placeholder}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[400px] p-0">
                    <Command shouldFilter={false}>
                        <CommandInput
                            onValueChange={setSearch}
                            placeholder="Search user groups..."
                        />
                        <CommandList>
                            {isLoading ? (
                                <div className="p-2 text-sm text-muted-foreground">
                                    Loading...
                                </div>
                            ) : (
                                <>
                                    <CommandEmpty>No user groups found.</CommandEmpty>
                                    <CommandGroup>
                                        {userGroups
                                            .filter((g) => g.id)
                                            .map((group, i) => {
                                                const isLast = i === userGroups.length - 1;
                                                return (
                                                    <CommandItem
                                                        key={group.id}
                                                        onSelect={() => handleSelect(group.id!)}
                                                        ref={isLast ? lastGroupRef : undefined}
                                                        value={group.id!}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                value.includes(group.id!)
                                                                    ? "opacity-100"
                                                                    : "opacity-0"
                                                            )}
                                                        />
                                                        <div className="flex flex-col">
                                                            <span>{group.name}</span>
                                                            {group.description && (
                                                                <span className="text-xs text-muted-foreground">
                                                                    {group.description}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </CommandItem>
                                                );
                                            })}
                                    </CommandGroup>
                                    {isFetchingNextPage && hasNextPage && (
                                        <div className="p-2 text-sm text-muted-foreground">
                                            Loading more...
                                        </div>
                                    )}
                                </>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {selectedGroups.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {selectedGroups.map((group) => (
                        <Badge key={group.id} variant="secondary">
                            {group.name}
                            <button
                                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                onClick={() => handleRemove(group.id!)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handleRemove(group.id!);
                                    }
                                }}
                                type="button"
                                disabled={disabled}
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    );
}
